import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, FileText, Image as ImageIcon, Trash2, UploadCloud } from "lucide-react";
import { AsYouType, isValidPhoneNumber } from "libphonenumber-js";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRINGS = {
  pt: {
    back: "Voltar à página inicial",
    overline: "Recursos Humanos",
    title: "Venha trabalhar connosco.",
    subtitle:
      "Procuramos pessoas talentosas e apaixonadas por tecnologia para se juntarem à nossa equipa em Luanda. Preencha o formulário e a nossa equipa de RH entrará em contacto.",
    name: "Nome completo *",
    name_ph: "O seu nome",
    email: "Email *",
    email_ph: "exemplo@dominio.com",
    phone: "Telefone",
    phone_ph: "+244 ...",
    role: "Cargo a que se candidata *",
    role_ph: "Selecione",
    role_options: {
      tech: "Técnico de Telecomunicações",
      vsat: "Engenheiro VSAT / Wireless",
      sales: "Comercial",
      it: "Informática / Redes",
      admin: "Administrativo / Financeiro",
      intern: "Estágio profissional",
      other: "Outro",
    },
    experience: "Anos de experiência",
    experience_ph: "Ex.: 3",
    location: "Localidade",
    location_ph: "Luanda, Benguela…",
    education: "Habilitações académicas",
    education_ph: "Ex.: Licenciatura em Engenharia Electrotécnica",
    linkedin: "LinkedIn / Portfólio (opcional)",
    linkedin_ph: "https://linkedin.com/in/...",
    cv_label: "Currículo * (PDF · máx. 5 MB)",
    cv_ph: "Cole aqui um link público para o seu CV",
    cv_drop: "Clique ou arraste o seu CV em PDF",
    cv_change: "Trocar ficheiro",
    photo_label: "Foto de perfil * (JPG / PNG · máx. 3 MB)",
    photo_drop: "Clique ou arraste a sua foto",
    photo_change: "Trocar foto",
    file_remove: "Remover",
    file_too_large: "Ficheiro demasiado grande.",
    file_wrong_type: "Tipo de ficheiro inválido.",
    cv_required: "Anexe o seu currículo em PDF (obrigatório).",
    photo_required: "Anexe uma foto de perfil (obrigatório).",
    message: "Mensagem / motivações",
    message_ph: "Conte-nos brevemente sobre si e o que o motivou a candidatar-se…",
    consent:
      "Autorizo a TVCABSAT, Lda a guardar e tratar os meus dados pessoais para efeitos de processo de recrutamento.",
    submit: "Enviar candidatura",
    sending: "A enviar...",
    success: "Candidatura recebida. A nossa equipa de RH analisa e responde brevemente.",
    error: "Não foi possível enviar. Tente novamente ou contacte-nos por telefone.",
    required: "Preencha os campos obrigatórios e aceite o tratamento de dados.",
  },
  en: {
    back: "Back to home",
    overline: "Human Resources",
    title: "Come work with us.",
    subtitle:
      "We're looking for talented people passionate about technology to join our Luanda team. Fill in the form and our HR team will be in touch.",
    name: "Full name *",
    name_ph: "Your name",
    email: "Email *",
    email_ph: "you@domain.com",
    phone: "Phone",
    phone_ph: "+244 ...",
    phone_invalid: "Invalid phone number.",
    role: "Role you're applying for *",
    role_ph: "Select",
    role_options: {
      tech: "Telecom Technician",
      vsat: "VSAT / Wireless Engineer",
      sales: "Sales",
      it: "IT / Networking",
      admin: "Admin / Finance",
      intern: "Internship",
      other: "Other",
    },
    experience: "Years of experience",
    experience_ph: "e.g. 3",
    location: "Location",
    location_ph: "Luanda, Benguela…",
    education: "Education",
    education_ph: "e.g. BSc Electrical Engineering",
    linkedin: "LinkedIn / Portfolio (optional)",
    linkedin_ph: "https://linkedin.com/in/...",
    cv_label: "Resume * (PDF · max 5 MB)",
    cv_drop: "Click or drop your CV in PDF",
    cv_change: "Change file",
    photo_label: "Profile picture * (JPG / PNG · max 3 MB)",
    photo_drop: "Click or drop your photo",
    photo_change: "Change photo",
    file_remove: "Remove",
    file_too_large: "File too large.",
    cv_required: "Please attach your CV in PDF (required).",
    photo_required: "Please attach a profile picture (required).",
    file_wrong_type: "File type not accepted.",
    message: "Message / motivation",
    message_ph: "Tell us briefly about yourself and why you applied…",
    consent:
      "I authorize TVCABSAT, Lda to store and process my personal data for recruitment purposes.",
    submit: "Send application",
    sending: "Sending...",
    success: "Application received. Our HR team will review and reply soon.",
    error: "Could not send. Please try again or call us.",
    required: "Please fill the required fields and accept data processing.",
  },
  es: {
    back: "Volver al inicio",
    overline: "Recursos Humanos",
    title: "Ven a trabajar con nosotros.",
    subtitle:
      "Buscamos personas talentosas apasionadas por la tecnología para unirse a nuestro equipo en Luanda. Completa el formulario y nuestro equipo de RR. HH. te contactará.",
    name: "Nombre completo *",
    name_ph: "Tu nombre",
    email: "Email *",
    email_ph: "tu@dominio.com",
    phone: "Teléfono",
    phone_ph: "+244 ...",
    phone_invalid: "Número de teléfono no válido.",
    role: "Cargo al que postula *",
    role_ph: "Selecciona",
    role_options: {
      tech: "Técnico de Telecomunicaciones",
      vsat: "Ingeniero VSAT / Wireless",
      sales: "Comercial",
      it: "Informática / Redes",
      admin: "Admin / Finanzas",
      intern: "Pasantía",
      other: "Otro",
    },
    experience: "Años de experiencia",
    experience_ph: "Ej.: 3",
    location: "Localidad",
    location_ph: "Luanda, Benguela…",
    education: "Estudios",
    education_ph: "Ej.: Lic. en Ingeniería Eléctrica",
    linkedin: "LinkedIn / Portafolio (opcional)",
    linkedin_ph: "https://linkedin.com/in/...",
    cv_label: "CV * (PDF · máx. 5 MB)",
    cv_drop: "Haz clic o arrastra tu CV en PDF",
    cv_change: "Cambiar archivo",
    photo_label: "Foto de perfil * (JPG / PNG · máx. 3 MB)",
    photo_drop: "Haz clic o arrastra tu foto",
    photo_change: "Cambiar foto",
    file_remove: "Eliminar",
    file_too_large: "Archivo demasiado grande.",
    file_wrong_type: "Tipo de archivo no aceptado.",
    cv_required: "Adjunta tu CV en PDF (obligatorio).",
    photo_required: "Adjunta una foto de perfil (obligatorio).",
    message: "Mensaje / motivación",
    message_ph: "Cuéntanos brevemente sobre ti y por qué postulaste…",
    consent:
      "Autorizo a TVCABSAT, Lda a almacenar y tratar mis datos personales para efectos de reclutamiento.",
    submit: "Enviar candidatura",
    sending: "Enviando...",
    success: "Candidatura recibida. Nuestro equipo de RR. HH. la revisará pronto.",
    error: "No se pudo enviar. Inténtalo de nuevo o llámanos.",
    required: "Completa los campos obligatorios y acepta el tratamiento de datos.",
  },
};

const inputCls =
  "w-full rounded-xl border border-slate-400 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-500 transition-all focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/15";

export default function Careers() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const s = STRINGS[lang] || STRINGS.pt;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    experience: "",
    location: "",
    education: "",
    linkedin: "",
    message: "",
  });
  const [cvFile, setCvFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const cvInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // Phone field uses Angola (AO) formatting & validation by default — the
  // careers form is targeted at local candidates. AsYouType handles both the
  // bare national number ("923 456 789") and the full E.164 form ("+244 923 456 789").
  const onPhoneChange = (e) => {
    const formatted = new AsYouType("AO").input(e.target.value);
    setForm((f) => ({ ...f, phone: formatted }));
  };

  const phoneValidity = useMemo(() => {
    const raw = form.phone.trim();
    if (!raw) return "empty";
    const e164 = raw.startsWith("+") ? raw : `+244${raw.replace(/\D/g, "")}`;
    try {
      return isValidPhoneNumber(e164) ? "valid" : "invalid";
    } catch {
      return "invalid";
    }
  }, [form.phone]);

  const handleCvSelect = (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error(s.file_wrong_type);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(s.file_too_large);
      return;
    }
    setCvFile(file);
  };

  const handlePhotoSelect = (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error(s.file_wrong_type);
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error(s.file_too_large);
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.role || !consent) {
      toast.error(s.required);
      return;
    }
    if (phoneValidity === "invalid") {
      toast.error(s.phone_invalid);
      return;
    }
    if (!cvFile) {
      toast.error(s.cv_required);
      return;
    }
    if (!photoFile) {
      toast.error(s.photo_required);
      return;
    }
    setSubmitting(true);
    try {
      const roleLabel = s.role_options[form.role] || form.role;
      const body = new FormData();
      body.append("name", form.name);
      body.append("email", form.email);
      body.append("phone", form.phone || "");
      body.append("role", roleLabel);
      body.append("experience", form.experience || "");
      body.append("location", form.location || "");
      body.append("education", form.education || "");
      body.append("linkedin", form.linkedin || "");
      body.append("message", form.message || "");
      if (cvFile) body.append("cv", cvFile);
      if (photoFile) body.append("photo", photoFile);

      const { data } = await axios.post(`${API}/careers/apply`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(s.success);
      // Navigate to the confirmation page with the reference (e.g. TVC-2026-001).
      // We pass it both via route state (clean URL) and as a query param so
      // the visitor can refresh or share the link without losing the ref.
      const ref = data?.reference;
      if (ref) {
        navigate(`/carreiras/recebida?ref=${encodeURIComponent(ref)}`, {
          state: { reference: ref },
        });
        return;
      }
      // Fallback (older backend that didn't return a reference): reset form in place.
      setForm({
        name: "",
        email: "",
        phone: "",
        role: "",
        experience: "",
        location: "",
        education: "",
        linkedin: "",
        message: "",
      });
      setCvFile(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      setConsent(false);
    } catch {
      toast.error(s.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#dfecf6] via-[#d8e8f5] to-[#d6e6f2]">
      <Header />

      <section className="relative scroll-mt-[20px] pb-20 pt-12 md:pt-16 lg:pb-24 lg:pt-20">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 lg:px-16">
          <Link
            to="/"
            data-testid="careers-back-link"
            className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 hover:text-blue-900"
          >
            <ArrowLeft className="h-4 w-4" /> {s.back}
          </Link>

          <div className="mt-6 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-blue-600">
                {s.overline}
              </div>
              <h1
                data-testid="careers-title"
                className="font-heading mt-5 text-3xl font-bold tracking-tight text-slate-900 md:text-4xl lg:text-5xl"
              >
                {s.title}
              </h1>
              <p className="mt-6 text-base leading-relaxed text-slate-600 md:text-lg">
                {s.subtitle}
              </p>
            </div>

            <form
              onSubmit={onSubmit}
              data-testid="careers-form"
              className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-xl shadow-slate-900/5 backdrop-blur lg:col-span-7 lg:p-10"
            >
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    {s.name}
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder={s.name_ph}
                    className={inputCls}
                    data-testid="careers-input-name"
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    {s.email}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder={s.email_ph}
                    className={inputCls}
                    data-testid="careers-input-email"
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    {s.phone}
                  </label>
                  <div className="relative">
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={onPhoneChange}
                      placeholder={s.phone_ph}
                      className={`${inputCls} pr-10 ${
                        phoneValidity === "valid"
                          ? "border-emerald-500 focus:border-emerald-500 focus:ring-emerald-500/20"
                          : ""
                      } ${
                        phoneValidity === "invalid"
                          ? "border-rose-400 focus:border-rose-500 focus:ring-rose-500/20"
                          : ""
                      }`}
                      data-testid="careers-input-phone"
                      aria-invalid={phoneValidity === "invalid"}
                    />
                    {phoneValidity === "valid" && (
                      <CheckCircle2
                        className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-emerald-500"
                        strokeWidth={2.5}
                        data-testid="careers-phone-valid"
                      />
                    )}
                    {phoneValidity === "invalid" && (
                      <AlertTriangle
                        className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-rose-500"
                        strokeWidth={2.5}
                        data-testid="careers-phone-invalid"
                      />
                    )}
                  </div>
                  {phoneValidity === "invalid" && (
                    <p className="mt-1.5 text-[12px] text-rose-600" data-testid="careers-phone-invalid-hint">
                      {s.phone_invalid}
                    </p>
                  )}
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    {s.role}
                  </label>
                  <select
                    name="role"
                    value={form.role}
                    onChange={onChange}
                    className={`${inputCls} appearance-none`}
                    data-testid="careers-input-role"
                  >
                    <option value="">{s.role_ph}</option>
                    {Object.entries(s.role_options).map(([k, label]) => (
                      <option key={k} value={k}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    {s.experience}
                  </label>
                  <input
                    name="experience"
                    value={form.experience}
                    onChange={onChange}
                    placeholder={s.experience_ph}
                    className={inputCls}
                    data-testid="careers-input-experience"
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    {s.location}
                  </label>
                  <input
                    name="location"
                    value={form.location}
                    onChange={onChange}
                    placeholder={s.location_ph}
                    className={inputCls}
                    data-testid="careers-input-location"
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    {s.education}
                  </label>
                  <input
                    name="education"
                    value={form.education}
                    onChange={onChange}
                    placeholder={s.education_ph}
                    className={inputCls}
                    data-testid="careers-input-education"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    {s.linkedin}
                  </label>
                  <input
                    name="linkedin"
                    value={form.linkedin}
                    onChange={onChange}
                    placeholder={s.linkedin_ph}
                    className={inputCls}
                    data-testid="careers-input-linkedin"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-2 flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    <FileText className="h-3.5 w-3.5 text-blue-600" />
                    {s.cv_label}
                  </label>
                  <input
                    ref={cvInputRef}
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => handleCvSelect(e.target.files?.[0])}
                    className="hidden"
                    data-testid="careers-input-cv"
                  />
                  {cvFile ? (
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50/60 px-4 py-3 text-sm">
                      <div className="flex min-w-0 items-center gap-3">
                        <FileText className="h-5 w-5 shrink-0 text-blue-600" />
                        <div className="min-w-0">
                          <div className="truncate font-semibold text-slate-900">
                            {cvFile.name}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {(cvFile.size / 1024).toFixed(1)} KB · PDF
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => cvInputRef.current?.click()}
                          className="text-[11px] font-semibold text-blue-700 hover:underline"
                        >
                          {s.cv_change}
                        </button>
                        <button
                          type="button"
                          onClick={() => setCvFile(null)}
                          aria-label={s.file_remove}
                          className="rounded-full p-1.5 text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => cvInputRef.current?.click()}
                      data-testid="careers-cv-dropzone"
                      className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center transition-all hover:border-blue-400 hover:bg-blue-50"
                    >
                      <UploadCloud className="h-7 w-7 text-blue-600" />
                      <div className="text-sm font-semibold text-slate-700">
                        {s.cv_drop}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        PDF · 5 MB
                      </div>
                    </button>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="mb-2 flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    <ImageIcon className="h-3.5 w-3.5 text-blue-600" />
                    {s.photo_label}
                  </label>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handlePhotoSelect(e.target.files?.[0])}
                    className="hidden"
                    data-testid="careers-input-photo"
                  />
                  {photoFile ? (
                    <div className="flex items-center gap-4 rounded-xl border border-blue-200 bg-blue-50/60 px-4 py-3 text-sm">
                      <img
                        src={photoPreview}
                        alt="preview"
                        className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-white"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-semibold text-slate-900">
                          {photoFile.name}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {(photoFile.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => photoInputRef.current?.click()}
                          className="text-[11px] font-semibold text-blue-700 hover:underline"
                        >
                          {s.photo_change}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setPhotoFile(null);
                            setPhotoPreview(null);
                          }}
                          aria-label={s.file_remove}
                          className="rounded-full p-1.5 text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      data-testid="careers-photo-dropzone"
                      className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center transition-all hover:border-blue-400 hover:bg-blue-50"
                    >
                      <ImageIcon className="h-7 w-7 text-blue-600" />
                      <div className="text-sm font-semibold text-slate-700">
                        {s.photo_drop}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        JPG, PNG ou WebP · 3 MB
                      </div>
                    </button>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    {s.message}
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    placeholder={s.message_ph}
                    rows={5}
                    className={`${inputCls} resize-none`}
                    data-testid="careers-input-message"
                  />
                </div>
              </div>

              <label className="mt-5 flex items-start gap-2.5 text-xs leading-relaxed text-slate-600">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  data-testid="careers-input-consent"
                />
                <span>{s.consent}</span>
              </label>

              <button
                type="submit"
                disabled={submitting}
                data-testid="careers-submit-button"
                className="group mt-6 inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-blue-500 hover:shadow-blue-600/40 active:scale-95 disabled:opacity-60"
              >
                <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />
                {submitting ? s.sending : s.submit}
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
